import React, { ReactNode } from 'react'
import NextLink from 'next/link'
import {
  Box,
  Flex,
  Image,
  Link,
  LinkBox,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'

import { Article } from '@/features/education/article'
import { IPost } from '@/types/ghostData.interface'

type Props = {
  posts: IPost[]
  slug: string
  title: string
  description: string
  button?: ReactNode
  stretchImage?: boolean
}

export const CategoryPosts = ({
  posts,
  slug,
  title,
  description,
  button,
  stretchImage = true,
}: Props) => {
  return (
    <Box>
      <Flex
        direction={{ base: 'column', lg: 'row' }}
        justifyContent={{ base: 'flex-start', lg: 'space-between' }}
        mb={6}
      >
        <Box mb={{ base: 4, lg: 0 }}>
          <Text
            variant="spaceGrotesk"
            fontSize="40px"
            lineHeight="normal"
            mb={1.5}
          >
            {title}
          </Text>
          <Text
            lineHeight={{ base: 1, lg: 2.5 }}
            color={useColorModeValue('blackAlpha.700', 'gray.460')}
          >
            {description}
          </Text>
        </Box>
        {button && (
          <Box alignSelf={{ base: 'flex-start', lg: 'flex-end' }}>{button}</Box>
        )}
      </Flex>

      <Stack
        direction={{ base: 'column', lg: 'row' }}
        justifyContent="space-between"
        spacing={{ base: 6, md: 8, xl: 16 }}
      >
        {posts?.map((article: IPost) => {
          return (
            <NextLink href={`/education/${article.slug}`} key={article.id}>
              <LinkBox
                w="full"
                cursor="pointer"
                minHeight="372px"
                borderColor="whiteAlpha.500"
                borderWidth="1px"
                color="white"
                bg="gray.940"
                boxShadow="0 1px 10px 0 rgba(0, 0, 0, 0.25)"
                _hover={{
                  textDecoration: 'none',
                  borderColor: 'brand.600',
                  boxShadow: '0 2px 15px 0 rgba(0, 0, 0, 0.25)',
                }}
              >
                {article.feature_image && (
                  <Box height="216px">
                    <Image
                      src={article.feature_image}
                      alt={article.title}
                      width="100%"
                      height="100%"
                      objectFit={stretchImage ? 'cover' : 'contain'}
                      objectPosition={stretchImage ? 'top right' : 'center'}
                    />
                  </Box>
                )}
                <Box py={5} px={3.5}>
                  <Text
                    mb={5}
                    fontSize="22px"
                    fontWeight={500}
                    letterSpacing="-0.6px"
                    lineHeight={1.36}
                  >
                    {article.title}
                  </Text>
                  <Text
                    fontSize="14px"
                    lineHeight={1.28}
                    letterSpacing="-0.2px"
                  >
                    {article.excerpt}
                  </Text>
                </Box>
              </LinkBox>
            </NextLink>
          )
        })}
      </Stack>
      <Flex
        direction="row"
        justifyContent={{ base: 'center', lg: 'flex-end' }}
        my={6}
      >
        <NextLink href={`/education/tag/${slug}`} passHref>
          <Link
            color={useColorModeValue('blackAlpha.800', 'gray.460')}
            textDecoration="underline"
            lineHeight={2.5}
            _hover={{ color: 'brand.600' }}
          >
            View More →
          </Link>
        </NextLink>
      </Flex>
    </Box>
  )
}
